/**
 * Patient report type registry.
 *
 * Each entry describes one generatable report. The builder renders the type
 * cards, the column toggles and the preview entirely from this array —
 * adding a future report type is a single new entry here.
 *
 * Entry shape:
 *   id            - stable key, stored on generated report history docs
 *   label         - Hebrew display name
 *   description   - one-liner shown on the type card
 *   Icon          - phosphor icon component
 *   scope         - "patient" (requires picking a patient) | "clinic" (whole clinic)
 *   flow          - "table" (default: dates → columns → table preview) |
 *                   "finance" (dates → statuses/charts/sections → KPI+charts preview → PDF)
 *   requiresFinance - card hidden without finance access
 *   editable      - whether the preview allows inline edits
 *   dataNeeds     - optional { treatments: true } extra datasets to fetch
 *   columns       - [{ key, label, defaultOn, finance? }] — finance columns
 *                   are hidden entirely when the user lacks finance access
 *   buildRows     - pure function ({ appointments, transactions, treatments,
 *                   patient, patients }) → array of row objects keyed by
 *                   column key (display strings)
 *   PreviewComponent - optional custom preview; null → generic table
 */

import { ClipboardText, UsersThree, CurrencyCircleDollar } from "@phosphor-icons/react";
import { formatHebrewDate, formatTime } from "../../../../utils/dateUtils";
import { sortPatientsByName } from "../../../../utils/patientSortUtils";

// Appointment status → Hebrew label (calendar statuses)
export const APPOINTMENT_STATUS_LABELS = {
  scheduled: "מתוכננת",
  completed: "התקיימה",
  cancelled: "בוטלה",
  pending_approval: "ממתינה לאישור",
};

// Financial transaction status → Hebrew label (matches finance page chips)
export const PAYMENT_STATUS_LABELS = {
  expected: "עתידי",
  pending: "ממתין לתשלום",
  pending_collection: "ממתין לתשלום",
  paid: "שולם",
  invoice_manual: "חשבונית",
  invoice_auto: "חשבונית",
  split: "פוצל",
  refunded: "זיכוי",
  cancelled: "בוטל",
};

const PAID_LIKE = new Set(["paid", "invoice_manual", "invoice_auto"]);

const formatFee = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const num = Number(amount);
  if (Number.isNaN(num)) return "—";
  return `₪${num.toLocaleString("he-IL")}`;
};

const fullName = (p) =>
  p ? `${p.firstName || ""} ${p.lastName || ""}`.trim() : "";

// ─── כרטיס מעקב טיפולים — single patient, one row per appointment ───
const buildTreatmentTrackingRows = ({ appointments = [], transactions = [], patient }) => {
  const txByAppointment = new Map();
  transactions.forEach((tx) => {
    if (tx?.appointmentId) txByAppointment.set(tx.appointmentId, tx);
  });

  const patientName = fullName(patient);

  return appointments.map((apt) => {
    const tx = txByAppointment.get(apt.id);
    const isCancelled = apt.status === "cancelled";

    let paymentStatus = "—";
    if (tx) {
      paymentStatus = PAYMENT_STATUS_LABELS[tx.status] || tx.status || "—";
    } else if (!isCancelled) {
      paymentStatus = "לא שולם";
    }

    return {
      patientName: patientName || apt.patientName || "—",
      sessionDate: apt.startTime
        ? `${formatHebrewDate(apt.startTime)} ${formatTime(apt.startTime)}`
        : "—",
      status: APPOINTMENT_STATUS_LABELS[apt.status] || apt.status || "—",
      treatmentType: apt.treatmentTypeName || "—",
      therapistName: apt.therapistName || "—",
      sessionFee: formatFee(tx?.amount ?? apt.paymentAmount),
      paymentStatus,
    };
  });
};

// ─── סקירת כל המטופלים — whole clinic, one row per patient ───
const buildAllPatientsOverviewRows = ({
  appointments = [],
  transactions = [],
  treatments = [],
  patients = [],
}) => {
  const byPatient = new Map();
  const bucket = (pid) => {
    if (!byPatient.has(pid)) {
      byPatient.set(pid, {
        total: 0,
        completed: 0,
        cancelled: 0,
        scheduled: 0,
        treatments: 0,
        paid: 0,
        pending: 0,
      });
    }
    return byPatient.get(pid);
  };

  appointments.forEach((apt) => {
    if (!apt.patientId) return;
    const b = bucket(apt.patientId);
    b.total += 1;
    if (apt.status === "completed") b.completed += 1;
    else if (apt.status === "cancelled") b.cancelled += 1;
    else b.scheduled += 1;
  });

  // Only finalized (locked) reports count; drafts aren't treatment summaries yet.
  treatments.forEach((t) => {
    if (t?.patientId && t.status === "locked") bucket(t.patientId).treatments += 1;
  });

  transactions.forEach((tx) => {
    if (!tx?.patientId) return;
    const b = bucket(tx.patientId);
    const amount = Number(tx.amount) || 0;
    if (PAID_LIKE.has(tx.status)) b.paid += amount;
    else if (tx.status === "pending" || tx.status === "pending_collection")
      b.pending += amount;
  });

  // Every clinic patient gets a row (zeros included) so the overview is a
  // complete census, not just the active slice.
  return sortPatientsByName(patients).map((p) => {
    const b = byPatient.get(p.id) || {
      total: 0,
      completed: 0,
      cancelled: 0,
      scheduled: 0,
      treatments: 0,
      paid: 0,
      pending: 0,
    };
    return {
      patientName: fullName(p) || "—",
      patientStatus: p.isActive === false ? "לא פעיל" : "פעיל",
      meetings: String(b.total),
      completed: String(b.completed),
      cancelled: String(b.cancelled),
      scheduled: String(b.scheduled),
      treatmentReports: String(b.treatments),
      paidAmount: formatFee(b.paid),
      pendingAmount: b.pending > 0 ? formatFee(b.pending) : "—",
    };
  });
};

const reportTypeRegistry = [
  {
    id: "treatmentTrackingCard",
    label: "כרטיס מעקב טיפולים",
    description: "כל הפגישות של מטופל אחד בטווח התאריכים, כולל סטטוס ותשלומים",
    Icon: ClipboardText,
    scope: "patient",
    editable: false,
    columns: [
      { key: "patientName", label: "שם המטופל", defaultOn: true },
      { key: "sessionDate", label: "תאריך ושעה", defaultOn: true },
      { key: "status", label: "סטטוס פגישה", defaultOn: true },
      { key: "treatmentType", label: "סוג טיפול", defaultOn: false },
      { key: "therapistName", label: "שם המטפל/ת", defaultOn: false },
      { key: "sessionFee", label: "עלות פגישה", defaultOn: true, finance: true },
      { key: "paymentStatus", label: "סטטוס תשלום", defaultOn: true, finance: true },
    ],
    buildRows: buildTreatmentTrackingRows,
    PreviewComponent: null,
  },
  {
    id: "allPatientsOverview",
    label: "סקירת כל המטופלים",
    description: "שורה לכל מטופל בקליניקה: פגישות, סטטוסים, סיכומי טיפול ותשלומים בטווח",
    Icon: UsersThree,
    scope: "clinic",
    editable: false,
    dataNeeds: { treatments: true },
    columns: [
      { key: "patientName", label: "שם המטופל", defaultOn: true },
      { key: "patientStatus", label: "סטטוס מטופל", defaultOn: false },
      { key: "meetings", label: 'סה"כ פגישות', defaultOn: true },
      { key: "completed", label: "התקיימו", defaultOn: true },
      { key: "cancelled", label: "בוטלו", defaultOn: true },
      { key: "scheduled", label: "מתוכננות", defaultOn: false },
      { key: "treatmentReports", label: "סיכומי טיפול", defaultOn: true },
      { key: "paidAmount", label: 'סה"כ שולם', defaultOn: true, finance: true },
      { key: "pendingAmount", label: "ממתין לתשלום", defaultOn: true, finance: true },
    ],
    buildRows: buildAllPatientsOverviewRows,
    PreviewComponent: null,
  },
  {
    id: "financeReport",
    label: "דוח כספים לייצוא",
    description:
      "דוח כספים מלא לטווח תאריכים: מדדים, גרפים, פירוט תנועות וסיכום לפי מטופל — לבחירה מה לכלול",
    Icon: CurrencyCircleDollar,
    scope: "clinic",
    flow: "finance",
    requiresFinance: true,
    editable: false,
    columns: [],
    buildRows: () => [],
    PreviewComponent: null,
  },
];

export const getReportType = (id) =>
  reportTypeRegistry.find((t) => t.id === id) || null;

/** Columns visible to this user (finance columns stripped without access). */
export const getVisibleColumns = (reportType, canAccessFinance) =>
  (reportType?.columns || []).filter((c) => !c.finance || canAccessFinance);

export default reportTypeRegistry;
